import { useEffect, useLayoutEffect, useRef, useState } from "react";
import type { ReactNode } from "react";
import {
  Crosshair,
  DraftingCompass,
  Eye,
  EyeOff,
  Lock,
  Minus,
  Plus,
  Type,
  Unlock,
} from "lucide-react";

import type { MeasurementType, ToolType } from "./registry";
import {
  MEASUREMENT_INCREMENT_OPTIONS,
  MEASUREMENT_TYPES_BY_TOOL,
  type MeasurementToolDefinition,
} from "./registry";
import { Button } from "@/components/ui/button";
import { DisabledReasonTooltip } from "@/components/ui/disabled-reason-tooltip";
import { Input } from "@/components/ui/input";
import { parseFeetInches } from "@/lib/geometry";
import { IconButton } from "@/components/ui/icon-button";
import { SegmentedControl } from "@/components/ui/segmented-control";
import {
  VIEWPORT_PANEL_EYEBROW_CLASS,
  VIEWPORT_TOOL_PRESSED_CLASSES,
  ViewportToolsetSubButton,
} from "@/viewport-tools/core";
import { DEFAULT_TEXT_FONT_SIZE } from "@/hooks/useMeasurementTextLock";
import { cn } from "@/lib/utils";

interface MeasurementToolsetPanelProps {
  measurementTools: MeasurementToolDefinition[];
  measurementToolsDisabled?: boolean;
  extraTools?: Array<{ id: string; label: string; icon: ReactNode }>;
  activeTool: ToolType | null;
  activeExtraToolId?: string | null;
  activeMeasurementType: MeasurementType | null;
  onToolSelect: (tool: ToolType) => void;
  onExtraToolSelect?: (toolId: string) => void;
  onTypeSelect: (type: MeasurementType) => void;
  snapEnabled: boolean;
  onSnapToggle: () => void;
  snapAvailable?: boolean;
  angleLockEnabled: boolean;
  onAngleLockToggle: () => void;
  incrementSnapEnabled: boolean;
  onIncrementSnapToggle: () => void;
  incrementStepFeet: number;
  onIncrementStepChange: (stepFeet: number) => void;
  showHeightInput: boolean;
  heightValue: string;
  onHeightChange: (value: string) => void;
  textLocked?: boolean;
  onTextLockToggle?: () => void;
  textFontSize?: number;
  onTextFontSizeChange?: (value: number) => void;
  labelsVisible?: boolean;
  onLabelsVisibleToggle?: () => void;
}

interface ToolSettingsPanelState {
  kind: "tool-settings";
}

interface AnnotationsPanelState {
  kind: "annotations";
}

type SecondaryPanelState = ToolSettingsPanelState | AnnotationsPanelState | null;

const SECONDARY_PANEL_BASE =
  "pointer-events-auto absolute left-full ms-1 z-10 w-44 rounded-md border border-border/70 bg-background/95 p-2 shadow-lg backdrop-blur";

function clampFontSize(value: number) {
  return Math.min(72, Math.max(1, Math.round(value * 2) / 2));
}

function measurementTypeRequiresHeight(tool: ToolType | null, measurementType: MeasurementType) {
  return (
    measurementType === "CF" ||
    ((tool === "line" || tool === "polyline") && measurementType === "SQFT")
  );
}

function formatIncrementSummaryLabel(stepFeet: number) {
  const fullLabel =
    MEASUREMENT_INCREMENT_OPTIONS.find((option) => option.stepFeet === stepFeet)?.label ?? `1'-0"`;
  return fullLabel.replace(`'-0"`, `'`);
}

function hasValidHeightValue(heightValue: string) {
  const parsedHeight = parseFeetInches(heightValue);
  return parsedHeight !== null && parsedHeight > 0;
}

export function MeasurementToolsetPanel({
  measurementTools,
  measurementToolsDisabled,
  extraTools = [],
  activeTool,
  activeExtraToolId,
  activeMeasurementType,
  onToolSelect,
  onExtraToolSelect,
  onTypeSelect,
  snapEnabled,
  onSnapToggle,
  snapAvailable = true,
  angleLockEnabled,
  onAngleLockToggle,
  incrementSnapEnabled,
  onIncrementSnapToggle,
  incrementStepFeet,
  onIncrementStepChange,
  showHeightInput,
  heightValue,
  onHeightChange,
  textLocked,
  onTextLockToggle,
  textFontSize,
  onTextFontSizeChange,
  labelsVisible = true,
  onLabelsVisibleToggle,
}: MeasurementToolsetPanelProps) {
  const panelRootRef = useRef<HTMLDivElement | null>(null);
  const toolButtonRefs = useRef<Partial<Record<ToolType, HTMLButtonElement | null>>>({});
  const annotationsButtonRef = useRef<HTMLButtonElement | null>(null);
  const heightInputRef = useRef<HTMLInputElement | null>(null);
  const previousActiveToolRef = useRef<ToolType | null>(activeTool);
  const [secondaryPanel, setSecondaryPanel] = useState<SecondaryPanelState>(
    activeTool ? { kind: "tool-settings" } : null
  );
  const [secondaryPanelTop, setSecondaryPanelTop] = useState("0px");
  const hasAnnotationsControls = Boolean(
    onTextLockToggle ||
      onTextFontSizeChange ||
      onLabelsVisibleToggle
  );

  useEffect(() => {
    if (previousActiveToolRef.current === activeTool) {
      return;
    }

    previousActiveToolRef.current = activeTool;
    if (activeTool) {
      setSecondaryPanel({ kind: "tool-settings" });
      return;
    }

    setSecondaryPanel((current) =>
      current?.kind === "tool-settings" ? null : current
    );
  }, [activeTool]);

  useEffect(() => {
    if (secondaryPanel?.kind === "tool-settings" && activeTool === null) {
      setSecondaryPanel(null);
      return;
    }

    if (secondaryPanel?.kind === "annotations" && !hasAnnotationsControls) {
      setSecondaryPanel(null);
    }
  }, [activeTool, hasAnnotationsControls, secondaryPanel]);

  useLayoutEffect(() => {
    const updateSecondaryPanelTop = () => {
      const rootRect = panelRootRef.current?.getBoundingClientRect();
      if (!secondaryPanel || !rootRect) {
        setSecondaryPanelTop("0px");
        return;
      }

      const trigger =
        secondaryPanel.kind === "tool-settings"
          ? activeTool
            ? toolButtonRefs.current[activeTool] ?? null
            : null
          : annotationsButtonRef.current;

      const triggerRect = trigger?.getBoundingClientRect();
      if (!triggerRect) {
        setSecondaryPanelTop("0px");
        return;
      }

      setSecondaryPanelTop(`${Math.round(triggerRect.top - rootRect.top)}px`);
    };

    updateSecondaryPanelTop();
    window.addEventListener("resize", updateSecondaryPanelTop);
    return () => window.removeEventListener("resize", updateSecondaryPanelTop);
  }, [measurementTools, secondaryPanel, hasAnnotationsControls]);

  const handleToolSelect = (toolId: ToolType) => {
    const isDeselectingActiveTool = activeTool === toolId;
    onToolSelect(toolId);
    setSecondaryPanel(isDeselectingActiveTool ? null : { kind: "tool-settings" });
  };

  const handleIncrementSelect = (stepFeet: number) => {
    if (!incrementSnapEnabled) {
      onIncrementSnapToggle();
    }
    onIncrementStepChange(stepFeet);
  };

  const currentTextFontSize = textFontSize ?? DEFAULT_TEXT_FONT_SIZE;
  const [fontSizeInput, setFontSizeInput] = useState(String(currentTextFontSize));
  const selectedIncrementSummary = formatIncrementSummaryLabel(incrementStepFeet);

  useEffect(() => {
    setFontSizeInput(String(currentTextFontSize));
  }, [currentTextFontSize]);

  useEffect(() => {
    if (
      secondaryPanel?.kind !== "tool-settings" ||
      !showHeightInput ||
      !hasValidHeightValue(heightValue)
    ) {
      return;
    }

    const handlePointerDown = (event: MouseEvent) => {
      const target = event.target;
      if (!(target instanceof Node)) {
        return;
      }

      if (panelRootRef.current?.contains(target)) {
        return;
      }

      setSecondaryPanel(null);
    };

    document.addEventListener("mousedown", handlePointerDown);
    return () => document.removeEventListener("mousedown", handlePointerDown);
  }, [heightValue, secondaryPanel, showHeightInput]);

  useEffect(() => {
    if (secondaryPanel?.kind !== "tool-settings" || !showHeightInput) {
      return;
    }

    heightInputRef.current?.focus();
    heightInputRef.current?.select();
  }, [secondaryPanel, showHeightInput, activeMeasurementType]);

  return (
    <div ref={panelRootRef} className="relative flex flex-col">
      {measurementTools.map((tool) => {
        const ToolIcon = tool.icon;

        return (
          <ViewportToolsetSubButton
            key={tool.id}
            buttonRef={(element) => {
              toolButtonRefs.current[tool.id] = element;
            }}
            icon={<ToolIcon className="size-4" />}
            label={tool.label}
            sublabel={
              activeTool === tool.id && activeMeasurementType ? (
                <>
                  <span>{activeMeasurementType}</span>
                  <span aria-hidden="true">-</span>
                  <span>{selectedIncrementSummary}</span>
                  {snapEnabled ? <Crosshair className="size-2.5" /> : null}
                  {angleLockEnabled ? <DraftingCompass className="size-2.5" /> : null}
                </>
              ) : undefined
            }
            sublabelAriaLabel={
              activeTool === tool.id && activeMeasurementType
                ? `${activeMeasurementType}, ${selectedIncrementSummary}${snapEnabled ? ", object snap enabled" : ""}${angleLockEnabled ? ", angle lock enabled" : ""}`
                : undefined
            }
            active={activeTool === tool.id}
            disabled={measurementToolsDisabled}
            onClick={() => handleToolSelect(tool.id)}
          />
        );
      })}

      {extraTools.map((tool) => (
        <ViewportToolsetSubButton
          key={tool.id}
          icon={tool.icon}
          label={tool.label}
          active={activeExtraToolId === tool.id}
          onClick={() => onExtraToolSelect?.(tool.id)}
        />
      ))}

      {hasAnnotationsControls ? (
        <ViewportToolsetSubButton
          buttonRef={annotationsButtonRef}
          icon={<Type className="size-4" />}
          label="Annotations"
          buttonClassName="border-b border-border/70"
          active={secondaryPanel?.kind === "annotations"}
          onClick={() =>
            setSecondaryPanel((current) =>
              current?.kind === "annotations" ? null : { kind: "annotations" }
            )
          }
        />
      ) : null}

      {secondaryPanel?.kind === "tool-settings" && activeTool ? (
        <div className={SECONDARY_PANEL_BASE} style={{ top: secondaryPanelTop }}>
          <div className={cn("mb-1.5", VIEWPORT_PANEL_EYEBROW_CLASS)}>
            Units
          </div>
          <SegmentedControl<MeasurementType>
            aria-label="Units"
            size="compact"
            className="w-full *:flex-1"
            value={activeMeasurementType ?? MEASUREMENT_TYPES_BY_TOOL[activeTool][0]}
            onValueChange={onTypeSelect}
            onItemClick={(type) => {
              // Picking a unit (even the current one) finishes the menu unless it needs a height.
              if (!measurementTypeRequiresHeight(activeTool, type)) {
                setSecondaryPanel(null);
              }
            }}
            options={MEASUREMENT_TYPES_BY_TOOL[activeTool].map((type) => ({
              value: type,
              label: type,
            }))}
          />
          <div className="mt-2.5">
            <div className={cn("mb-1", VIEWPORT_PANEL_EYEBROW_CLASS)}>
              Drawing aids
            </div>
            <DisabledReasonTooltip
              className="flex w-full"
              side="right"
              reason={
                snapAvailable
                  ? undefined
                  : "Object snap is unavailable until snap geometry is indexed for this document"
              }
            >
              <Button
                type="button"
                aria-pressed={snapEnabled}
                variant="ghost"
                size="compact"
                className={cn("w-full justify-start", VIEWPORT_TOOL_PRESSED_CLASSES)}
                disabled={!snapAvailable}
                onClick={onSnapToggle}
              >
                <Crosshair className="size-3" />
                Object snap
              </Button>
            </DisabledReasonTooltip>
            <Button
              type="button"
              aria-pressed={angleLockEnabled}
              variant="ghost"
              size="compact"
              className={cn("mt-1 w-full justify-start", VIEWPORT_TOOL_PRESSED_CLASSES)}
              onClick={onAngleLockToggle}
            >
              <DraftingCompass className="size-3" />
              Angle lock
            </Button>
          </div>
          <div className="mt-2.5">
            <div className={cn("mb-1", VIEWPORT_PANEL_EYEBROW_CLASS)}>
              Increments
            </div>
            <Button
              type="button"
              aria-pressed={incrementSnapEnabled}
              variant="ghost"
              size="compact"
              className={cn("mb-1 w-full justify-start", VIEWPORT_TOOL_PRESSED_CLASSES)}
              onClick={onIncrementSnapToggle}
            >
              Increment snap
            </Button>
            <SegmentedControl
              aria-label="Increment step"
              size="compact"
              className="w-full *:flex-1"
              value={String(incrementStepFeet)}
              onValueChange={(step) => handleIncrementSelect(Number(step))}
              onItemClick={(step) => {
                // Re-picking the current step still turns increment snap on.
                if (Number(step) === incrementStepFeet && !incrementSnapEnabled) {
                  onIncrementSnapToggle();
                }
              }}
              options={MEASUREMENT_INCREMENT_OPTIONS.map((option) => ({
                value: String(option.stepFeet),
                label: option.label,
              }))}
            />
          </div>
          {showHeightInput ? (
            <div className="mt-2.5">
              <div className={cn("mb-1", VIEWPORT_PANEL_EYEBROW_CLASS)}>
                Height
              </div>
              <Input
                ref={heightInputRef}
                type="text"
                aria-label="Measurement height"
                value={heightValue}
                onChange={(event) => onHeightChange(event.target.value)}
                placeholder={'e.g. 8\'-0"'}
                className="h-7 rounded-sm px-2 text-xs"
              />
            </div>
          ) : null}
        </div>
      ) : null}

      {hasAnnotationsControls && secondaryPanel?.kind === "annotations" ? (
        <div className={SECONDARY_PANEL_BASE} style={{ top: secondaryPanelTop }}>
          {onTextLockToggle ? (
            <>
              <div className={cn("mb-2", VIEWPORT_PANEL_EYEBROW_CLASS)}>
                Text Lock
              </div>
              <div className="grid grid-cols-2 gap-1">
                {/* Lock is the current document/PDF-relative mode, not an action label. Keep
                    these conditions aligned with getMeasurementLabelScale and PDF fontSize. */}
                <Button
                  type="button"
                  variant="ghost"
                  size="compact"
                  aria-pressed={Boolean(textLocked)}
                  className={VIEWPORT_TOOL_PRESSED_CLASSES}
                  onClick={() => {
                    if (!textLocked) {
                      onTextLockToggle();
                    }
                  }}
                >
                  <Lock className="size-3.5" />
                  Lock
                </Button>
                <Button
                  type="button"
                  variant="ghost"
                  size="compact"
                  aria-pressed={!textLocked}
                  className={VIEWPORT_TOOL_PRESSED_CLASSES}
                  onClick={() => {
                    if (textLocked) {
                      onTextLockToggle();
                    }
                  }}
                >
                  <Unlock className="size-3.5" />
                  Unlock
                </Button>
              </div>
            </>
          ) : null}

          {onLabelsVisibleToggle ? (
            <div className={cn(onTextLockToggle ? "mt-3" : null)}>
              <div className={cn("mb-2", VIEWPORT_PANEL_EYEBROW_CLASS)}>
                Visibility
              </div>
              <Button
                type="button"
                variant="ghost"
                size="compact"
                aria-pressed={Boolean(labelsVisible)}
                className={cn("w-full justify-start", VIEWPORT_TOOL_PRESSED_CLASSES)}
                onClick={onLabelsVisibleToggle}
              >
                {labelsVisible ? <Eye className="size-3.5" /> : <EyeOff className="size-3.5" />}
                Labels
              </Button>
            </div>
          ) : null}

          {onTextFontSizeChange ? (
            <div className={cn(onTextLockToggle || onLabelsVisibleToggle ? "mt-3" : null)}>
              <div className={cn("mb-2", VIEWPORT_PANEL_EYEBROW_CLASS)}>
                Font Size
              </div>
              <div className="flex items-stretch gap-1">
                <Input
                  type="number"
                  min={1}
                  max={72}
                  step={0.5}
                  aria-label="Measurement text size"
                  value={fontSizeInput}
                  onChange={(event) => {
                    if (event.target.value === "") {
                      setFontSizeInput("");
                      return;
                    }

                    setFontSizeInput(event.target.value);
                    const parsed = parseFloat(event.target.value);
                    if (!Number.isFinite(parsed) || parsed <= 0) {
                      return;
                    }
                    onTextFontSizeChange(clampFontSize(parsed));
                  }}
                  className="h-8 text-xs"
                />
                <div className="flex flex-col gap-1">
                  <IconButton
                    label="Increase measurement text size"
                    icon={Plus}
                    variant="outline"
                    size="xs"
                    tooltipSide="right"
                    className="h-[15px] w-5"
                    iconClassName="size-2.5"
                    onClick={() => {
                      const nextFontSize = clampFontSize(currentTextFontSize + 0.5);
                      setFontSizeInput(String(nextFontSize));
                      onTextFontSizeChange(nextFontSize);
                    }}
                  />
                  <IconButton
                    label="Decrease measurement text size"
                    icon={Minus}
                    variant="outline"
                    size="xs"
                    tooltipSide="right"
                    className="h-[15px] w-5"
                    iconClassName="size-2.5"
                    onClick={() => {
                      const nextFontSize = clampFontSize(currentTextFontSize - 0.5);
                      setFontSizeInput(String(nextFontSize));
                      onTextFontSizeChange(nextFontSize);
                    }}
                  />
                </div>
              </div>
            </div>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}
